import { memo, useState, useEffect, useMemo, useCallback } from "react";
import { OrderInspectionLog, OrderPicklist, OrderPicklistMovement, OrderReceipt, PageProps, ProductionOrder } from "../../components/types";
import { groupBy as rowGrouper } from 'lodash';
import {
  Tabs,
  TabItem,
} from "@aws-amplify/ui-react";
import { HiMagnifyingGlass } from "react-icons/hi2";
import Badge from '../../components/Badge';
import { CiReceipt } from "react-icons/ci";
import Modal from "../../components/Modal";
import RetainRecord from "../Form/RetainRecord";

import { getOrder, notifyOrderReceiptRejected } from '../../apis';
import {
  deleteInspectionLog,
  getComments,
  getFGInspectionLogsByOrder,
  getMovementsById,
  getOrderReceipts,
  otherApproveOrderReceipts,
  otherRejectOrderReceipts,
  postComment,
  updateStatusComment,
} from '../../states/PicklistAction'
import Comment from '../../components/Comment'
import { FaCheck } from "react-icons/fa";
import Header from './header'
import { useLocation, useHistory } from "react-router-dom";
import { formatDateFromUnix, formatDateObject } from "../../utilities/Format";
import { AvgFwtByLot, Department, ReceiptApprover } from "../../models";
import { BiCommentDetail } from "react-icons/bi";
import { MdFactory } from "react-icons/md";
import DataGrid, { FormatterProps, RowsChangeData } from 'react-data-grid'
import { getDeleteInspectionColumn, OrderInspectionLogColumns, PicklistItemRecordTotalColumn } from "../../constants/Column";
import { BsArrowReturnLeft } from "react-icons/bs";
import RejectApproval from "../Dialogs/RejectApproval";

type OrderReceiptState = {
  orderReceipts: OrderReceipt[]
}

enum APPROVAL_STATE {
  READY_TO_APPROVE,
  APPROVED,
  WAIT_FOR_PREVIOUS_APPROVE,
  DONT_ALLOW_APPROVE
}

const OrderReceiptApprovalDetail = ({ title, id, username, readOnly, department } : PageProps) => {
  const location = useLocation()
  const history = useHistory()
  
  const state = location.state as OrderReceiptState

  const [ productionOrder, setProductionOrder ] = useState<ProductionOrder>()
  const [ fgInspectionLogs, setFgInspectionLogs ] = useState<OrderInspectionLog[]>()
  const [ orderReceipts, setOrderReceipts ] = useState<OrderReceipt[]>([])
  const [ movements, setMovements ] = useState<OrderPicklistMovement[]>([])
  const [ comments, setComments ] = useState<OrderPicklist[]>([])
  const [ showConfirmDialog, setShowConfirmDialog ] = useState<boolean>(false)
  const [ showCancelDialog, setShowCancelDialog ] = useState<boolean>(false)
  const [ isLoading, setIsLoading ] = useState<boolean>(false)

  const [expandedGroupIds, setExpandedGroupIds] = useState<ReadonlySet<unknown>>(
    () => new Set<unknown>([''])
  );

  const openDialog = () => setShowConfirmDialog(true)
  const closeDialog = () => setShowConfirmDialog(false)

  const onApproved = async () => {
    setIsLoading(true)

    if (orderReceipts && orderReceipts.length > 0) {
      const approvedReceipts = await otherApproveOrderReceipts(orderReceipts, department as Department, username!)
      if (approvedReceipts.length !== orderReceipts.length) {
        alert('การอนุมัติ Receipt ไม่สำเร็จ กรุณาตรวจสอบอีกครั้ง')
      } else {
        setOrderReceipts(approvedReceipts)
      }
    }

    setIsLoading(false)
    closeDialog()
    history.goBack()
  }

  const onRejectReceipt = useCallback(async (rejectDepartment: ReceiptApprover, rejectComment: string) => {
    if (orderReceipts && orderReceipts.length > 0) {
      const rejectedReceipts = await otherRejectOrderReceipts(
        orderReceipts, rejectDepartment, rejectComment, `${department}-${username!}`)
      if (rejectedReceipts.length !== orderReceipts.length) {
        alert('การยกเลิกอนุมัติ Receipt ไม่สำเร็จ กรุณาตรวจสอบอีกครั้ง')
      } else {
        setOrderReceipts(rejectedReceipts)
        notifyOrderReceiptRejected(
          id!,
          department || '',
          rejectDepartment,
          formatDateObject(new Date(), 'yyyy-MM-dd HH:mm:ss'),
          rejectComment,
          productionOrder?.division || ''
        )
      }
    }
    setShowCancelDialog(false)
  }, [ orderReceipts, department, username, id, productionOrder ])

  useEffect(() => {
    const fetchOrderReceipts = async (id: string) => {
      const orderReceipts = await getOrderReceipts(id)
      setOrderReceipts(orderReceipts)
    };

    if (!state) {
      fetchOrderReceipts(id!);
    } else {
      setOrderReceipts(state.orderReceipts);
    }
  }, [ state, id ]);

  useEffect(() => {
    const fetchOrderResults = async (id: string) => {
      const productionOrder = await getOrder(id)
      setProductionOrder(productionOrder)

      if (id.startsWith('9')) {
        const fgInspectionLogs = await getFGInspectionLogsByOrder(id)
        setFgInspectionLogs(fgInspectionLogs)
      }

      const mvs = await getMovementsById(id)
      setMovements(mvs)
      const comments = await getComments(id)
      setComments(comments)
    }
    fetchOrderResults(id!)
  }, [ id ])

  useEffect(() => {
    if (movements.length > 0) {
      setExpandedGroupIds(new Set(movements
        .filter(mv => movements.filter(
          mv1 => mv1.materialCode === mv.materialCode).length > 1 || mv.returnGoodQty! > 0 || mv.returnWasteQty! > 0
        )
        .map(itm => itm.materialCode)
        ))
    }
  }, [ movements ])

  const updateOrderReceiptFromChild = useCallback((updatedReceipt: OrderReceipt) => {
    setOrderReceipts(orderReceipts?.map(log => 
      log.productionOrderId === updatedReceipt.productionOrderId &&
			log.batchLotNo === updatedReceipt.batchLotNo
			? updatedReceipt : log))
  },[ orderReceipts, setOrderReceipts ])

  const updateOrderReceiptsFromChild = useCallback((updatedReceipts: OrderReceipt[]) => {
    setOrderReceipts(updatedReceipts)
  }, [ setOrderReceipts ])

  const pdApprovedBy = useMemo(() => {
    return orderReceipts && orderReceipts.length > 0 && orderReceipts[0].approvedByPdBy ? 
      `${orderReceipts[0].approvedByPdBy} (${formatDateFromUnix(orderReceipts[0].approvedByPdDatetime)})` 
      : 
      ''
  }, [ orderReceipts ])

  const qcApprovedBy = useMemo(() => {
    return orderReceipts && orderReceipts.length > 0 && orderReceipts[0].approvedByQcBy ? 
      `${orderReceipts[0].approvedByQcBy} (${formatDateFromUnix(orderReceipts[0].approvedByQcDatetime)})` 
      : 
      ''
  }, [ orderReceipts ])

  const whApprovedBy = useMemo(() => {
    return orderReceipts && orderReceipts.length > 0 && orderReceipts[0].approvedByWhBy ? 
      `${orderReceipts[0].approvedByWhBy} (${formatDateFromUnix(orderReceipts[0].approvedByWhDatetime)})` 
      : 
      ''
  }, [ orderReceipts ])

  const faApprovedBy = useMemo(() => {
    return orderReceipts && orderReceipts.length > 0 && orderReceipts[0].approvedByFaBy ? 
      `${orderReceipts[0].approvedByFaBy} (${formatDateFromUnix(orderReceipts[0].approvedByFaDatetime)})` 
      : 
      ''
  }, [ orderReceipts ])

  const isRework = useMemo(() => orderReceipts && orderReceipts.length > 0 && orderReceipts[0].isRework, [ orderReceipts ])

  const onPostComment = useCallback(async (comment: string) => {
    const commentResponse = await postComment(
      id!,
      comment, 
      username!
    )
    setComments([ ...comments, commentResponse ])
  }, [ id, username, comments ])

  const onActiveComment = useCallback(async (orderId: string, skCommentId: string, active: boolean, version: number) => {
		const updatedComment = await updateStatusComment(orderId, skCommentId, active, version)
		setComments(comments.map(cmt => cmt.sk === skCommentId ? updatedComment : cmt))
	}, [ comments ])

  const withDrawItems = useMemo(() => new Set(movements.map(itm => itm.materialCode)), [ movements ])

  const onActionRows = async (rows: OrderInspectionLog[], 
    changedInfo: RowsChangeData<OrderInspectionLog>) => {
    const changedRowIdx = changedInfo.indexes[0]
    if (changedRowIdx < 0) return

    const changedRow = rows[changedRowIdx]

    if (changedInfo.column.key === 'delete') {
      const deleted = await deleteInspectionLog(changedRow.id, changedRow._version!);
      setFgInspectionLogs(fgInspectionLogs?.map(log => 
        log.id === deleted.id ? deleted : log))
    }
  };
  
  const activeInspectionLog = useMemo(() => fgInspectionLogs?.filter(log => !log._deleted) || [], [ fgInspectionLogs ])

  let fgInspectionColumns = Array.from(OrderInspectionLogColumns)
  fgInspectionColumns.push(getDeleteInspectionColumn(readOnly!, department as Department))

  const approvalState : APPROVAL_STATE = useMemo(() => {
    const localDep = department === 'Production' ? 'PD' : department === 'Warehouse' ? 'WH' : department
    const firstReceipt = orderReceipts && orderReceipts.length > 0 ? orderReceipts[0] : undefined

    if (!firstReceipt) return APPROVAL_STATE.DONT_ALLOW_APPROVE

    const currentApprover = firstReceipt ? firstReceipt.currentApprover : ReceiptApprover.PD

    if (readOnly) return APPROVAL_STATE.DONT_ALLOW_APPROVE
    if (localDep === currentApprover) return APPROVAL_STATE.READY_TO_APPROVE
    
    if (localDep === 'PD' && firstReceipt.approvedByPdDatetime! > 0) return APPROVAL_STATE.APPROVED
    if (localDep === 'QC' && firstReceipt.approvedByQcDatetime! > 0) return APPROVAL_STATE.APPROVED
    if (localDep === 'WH' && firstReceipt.approvedByWhDatetime! > 0) return APPROVAL_STATE.APPROVED
    if (localDep === 'FA' && firstReceipt.approvedByFaDatetime! > 0) return APPROVAL_STATE.APPROVED

    return APPROVAL_STATE.WAIT_FOR_PREVIOUS_APPROVE
  }, [ readOnly, orderReceipts, department ])

  return <div className="page-container">
  {productionOrder && <Header
    title={title}
    productionOrder={productionOrder}
    pdApprovedBy={pdApprovedBy!}
    qcApprovedBy={qcApprovedBy!}
    whApprovedBy={whApprovedBy!}
    faApprovedBy={faApprovedBy!}
    reworkInfo={isRework ? {
      isRework: isRework,
      reworkNote: orderReceipts![0].reworkNote || '',
      reworkedBy: orderReceipts![0].reworkedBy || '',
      reworkedDatetime: orderReceipts![0].reworkedDatetime || 0
    } : undefined}
  />}
  <div className='flex items-end justify-end my-3'>
    <div className='flex items-center gap-2'>
      <button
        className='btn-outline-secondary py-1.5 flex gap-2' 
        onClick={() => setShowCancelDialog(true)}
        disabled={approvalState !== APPROVAL_STATE.READY_TO_APPROVE}
      >
        <BsArrowReturnLeft />ไม่อนุมัติ
      </button>
      <button
        className=' btn-success w-48 py-1.5 flex gap-1'
        onClick={openDialog}
        disabled={approvalState !== APPROVAL_STATE.READY_TO_APPROVE}
      >
        <FaCheck />กดเพื่ออนุมัติ
      </button>
    </div>
  </div>
  {approvalState === APPROVAL_STATE.WAIT_FOR_PREVIOUS_APPROVE && <div className='flex items-center justify-end gap-2 font-bold text-red-600'>กรุณารอผู้อนุมัติ ก่อนหน้า ทำการอนุมัติก่อน</div>}
  {approvalState === APPROVAL_STATE.DONT_ALLOW_APPROVE && <div className='flex items-center justify-end gap-2 font-bold text-yellow-600'>ไม่มีสิทธิ์อนุมัติ</div>}
  {approvalState === APPROVAL_STATE.APPROVED && <div className='flex items-center justify-end gap-2 font-bold text-green-600'><FaCheck /> อนุมัติไปแล้ว</div>}
  <Tabs justifyContent="flex-start">
    <TabItem
      title={
        <div className="flex items-center justify-center gap-1">
          <CiReceipt />
          Receipt <Badge variant="info">{`${(orderReceipts || []).length}`}</Badge>
        </div>
      }
    >
      {productionOrder && 
      <RetainRecord
        isFilling={movements.filter(mv => mv.materialType === 'BULK').length > 0}
        title='Order Receipt'
        disabledInput={true}
        orderFromParent={productionOrder}
        receiptsFromParent={orderReceipts}
        setReceiptsToParent={updateOrderReceiptsFromChild}
        setReceiptToParent={updateOrderReceiptFromChild}
      />}
    </TabItem>
    <TabItem
      title={
        <div className="flex items-center justify-center gap-1">
          <HiMagnifyingGlass />
          รายการตรวจคุณภาพ <Badge variant="info">{`${activeInspectionLog.length}`}</Badge>
        </div>
      }
    >
      <DataGrid
        className='my-3'
        style={{ height: "100%", contain: "none" }}
        rows={activeInspectionLog}
        columns={fgInspectionColumns}
        defaultColumnOptions={{ resizable: true }}
        rowHeight={row => {
          const rowData = row.row as OrderInspectionLog
          const avgFwtByLots = rowData.avgFwtByLots ? (JSON.parse(rowData.avgFwtByLots) as AvgFwtByLot[]) : []
          if (avgFwtByLots.length === 0) return 35
          const expandedRowHeight = (avgFwtByLots.length * 40) + 15
          return expandedRowHeight
        }}
        onRowsChange={onActionRows}
      />
    </TabItem>
    <TabItem
      title={
        <div className="flex items-center justify-center gap-1">
          <MdFactory />
          รายการใช้ดี/เสีย <Badge variant="info">{`${withDrawItems.size}`}</Badge>
        </div>
      }
    >
      <DataGrid
        className='mb-3'
        style={{ height: "100%", contain: "none" }}
        columns={[...PicklistItemRecordTotalColumn, { 
          key: 'remark', name: 'หมายเหตุ', width: 180,
          formatter: (props: FormatterProps<OrderPicklistMovement>) => {
            if (props.row.returnGoodQty! > 0 || props.row.returnGoodShrinkQty! > 0 || props.row.returnWasteQty! > 0) {
              const returnRefId = props.row.returnRefId ?
                props.row.returnRefId.replace(/-/g, '') : ''
              return <h6 className='flex items-center h-full'>
                {returnRefId ? <Badge variant=' bg-green-600 text-xs text-white'>
                  <div className='flex items-center justify-center gap-1'>
                    <BsArrowReturnLeft />{`${props.row.returnReasonDescription || ''} (${returnRefId.slice(returnRefId.length - 8)})`}
                  </div>
                </Badge> : <Badge variant=' text-xs text-green-600 border border-green-600 font-bold'>{props.row.returnReasonDescription || ''}</Badge>}
              </h6>
            }
            return <></>
          }
        }]}
        rows={movements}
        rowKeyGetter={(row) => row.id}
        groupBy={['materialCode']}
        rowGrouper={rowGrouper}
        expandedGroupIds={expandedGroupIds}
        onExpandedGroupIdsChange={setExpandedGroupIds}
        defaultColumnOptions={{ resizable: true }}
        rowClass={row => row.returnGoodQty! > 0 || row.returnGoodShrinkQty! > 0 || row.returnWasteQty! > 0 ? 'bg-green-100':''}
        rowHeight={(arg) => {
          if (arg.type === 'GROUP' && arg.row.childRows[0].barcode) return 55
          return 35
        }}
        headerRowHeight={50}
      />
    </TabItem>
    <TabItem
        title={
          <div className="flex items-center justify-center gap-1">
            <BiCommentDetail />
            Comments <Badge variant="info">{`${comments.length}`}</Badge>
          </div>
        }
      >
        <Comment comments={comments.sort((a, b) => a.sk.localeCompare(b.sk))} onPost={onPostComment} onActive={onActiveComment} username={username}/>
      </TabItem>
  </Tabs>
  <Modal show={showConfirmDialog} cancelText='ยกเลิก'
    onHide={closeDialog}
    okText="อนุมัติ"
    okTheme='success'
    okAction={onApproved}
    title={`คุณกำลังอนุมัติใบ Receipt ของ ${id}`}
    loading={isLoading}
    >
      <div className="flex items-center gap-2">
        <FaCheck size={48} className="text-green-500 "/>
        <h6>คุณต้องการยืนยันอนุมติใช่หรือไม่ ?</h6>
      </div>
    
  </Modal>
  {showCancelDialog && <RejectApproval
    show={showCancelDialog}
    setShow={setShowCancelDialog}
    onActionConfirm={onRejectReceipt}
    productionOrder={id!}
    department={department as Department}
  />}
</div>
}

export default memo(OrderReceiptApprovalDetail);